import React, { Suspense, useEffect } from "react";
import "./App.css";
import "./styles/theme.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";

// Auth Components (loaded eagerly - needed for routing logic)
import AuthCallback from "./components/auth/AuthCallback";
import ProtectedRoute from "./components/auth/ProtectedRoute";

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-brand-black flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-brand-muted text-sm">Loading...</p>
    </div>
  </div>
);

// Scroll to top on every route change (desktop + mobile)
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

// Lazy-loaded pages (code splitting)
const HomePage = React.lazy(() => import("./pages/HomePage"));
const ExpertisePage = React.lazy(() => import("./pages/ExpertisePage"));
const ProjectsPage = React.lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = React.lazy(() => import("./pages/ProjectDetailPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const BlogPage = React.lazy(() => import("./pages/BlogPage"));
const BlogPostPage = React.lazy(() => import("./pages/BlogPostPage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const LegalPage = React.lazy(() => import("./pages/LegalPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const ThankYouPage = React.lazy(() => import("./pages/ThankYouPage"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
const ProViewLandingPage = React.lazy(() => import("./pages/ProViewLandingPage"));

// ProView authenticated pages
const ProViewLoginPage = React.lazy(() => import("./pages/proview/ProViewLoginPage"));
const ProViewDashboard = React.lazy(() => import("./pages/proview/ProViewDashboard"));
const CreateProfilePage = React.lazy(() => import("./pages/proview/CreateProfilePage"));
const ProfilePage = React.lazy(() => import("./pages/proview/ProfilePage"));
const MatchDetailPage = React.lazy(() => import("./pages/proview/MatchDetailPage"));
const QuickStartWizard = React.lazy(() => import("./pages/proview/QuickStartWizard"));
const StatsPage = React.lazy(() => import("./pages/proview/StatsPage"));
const SettingsPage = React.lazy(() => import("./pages/proview/SettingsPage"));
const PricingPage = React.lazy(() => import("./pages/proview/PricingPage"));
const TrainingPlannerPage = React.lazy(() => import("./pages/proview/TrainingPlannerPage"));

// App Router to handle auth callback detection
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - handle auth callback synchronously
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <ScrollToTop />
      <Routes>
        {/* Main Site Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/expertise" element={<ExpertisePage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:id" element={<BlogPostPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />

        {/* ProView Routes */}
        <Route path="/proview" element={<ProViewLandingPage />} />
        <Route path="/proview/login" element={<ProViewLoginPage />} />
        <Route path="/proview/dashboard" element={<ProtectedRoute><ProViewDashboard /></ProtectedRoute>} />
        <Route path="/proview/profile/create" element={<ProtectedRoute><CreateProfilePage /></ProtectedRoute>} />
        <Route path="/proview/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/proview/quick-start" element={<ProtectedRoute><QuickStartWizard /></ProtectedRoute>} />
        <Route path="/proview/stats" element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
        <Route path="/proview/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/proview/pricing" element={<PricingPage />} />
        <Route path="/proview/match/:matchId" element={<ProtectedRoute><MatchDetailPage /></ProtectedRoute>} />
        <Route path="/proview/training" element={<ProtectedRoute><TrainingPlannerPage /></ProtectedRoute>} />

        {/* Catch-all 404 route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" theme="dark" richColors />
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
